import { ButtonV2 } from '@cimplico/components';
import { ArrowRightIcon, ClockIcon } from '@heroicons/react/24/outline';
import { useState } from 'react';
import { useUpdateSubscription } from '../../../../queries/use-update-subscription';
import { useIsActiveSubscription } from '../../../../hooks/use-is-active-subscription';
import { Subscription } from '../../../../types/firm';
import { toastNotification } from '../../../../components/toast/Toast';
import { formatDate } from '../../../../utilities/formatting';
import SectionLabel from '../../../../components/section-label/SectionLabel';
import SettingsContainer from '../../../../components/settings-container/SettingsContainer';

type ScheduledUpdatesProps = {
    subscription: Subscription;
};

type CancelItem = 'tier' | 'duration' | 'quantity';

function ScheduledUpdates({ subscription }: ScheduledUpdatesProps): JSX.Element | null {
    const [cancelling, setCancelling] = useState<CancelItem>();
    const updateScheduleQuery = useUpdateSubscription(subscription.firm.id);
    const activeSubscription = useIsActiveSubscription();

    function cancelSchedule(cancel: CancelItem): void {
        setCancelling(cancel);
        updateScheduleQuery.mutate(
            {
                duration: cancel === 'duration' ? subscription.duration : subscription.schedule?.duration || undefined,
                tier: cancel === 'tier' ? subscription.tier : subscription.schedule?.tier || undefined,
                quantity:
                    cancel === 'quantity'
                        ? subscription.activeEntities.allocation
                        : subscription.schedule?.activeEntityAllocation || undefined,
            },
            {
                onError: () => {
                    toastNotification('error', 'Unable to remove the scheduled update');
                },
                onSuccess: () => {
                    toastNotification('success', 'Successfully removed the scheduled update');
                },
                onSettled: () => {
                    setCancelling(undefined);
                },
            }
        );
    }

    if (!subscription.schedule) {
        return null;
    }

    const schedule = subscription.schedule;

    const description = [];
    if (schedule.tier) {
        description.push(`Changing tier from ${subscription.tier} to ${schedule.tier}`);
    }
    if (schedule.duration) {
        description.push(`Changing billing duration from ${subscription.duration} to ${schedule.duration}`);
    }

    return (
        <SettingsContainer>
            <SectionLabel
                title="Scheduled updates"
                description="Manage scheduled updates to your subscription, including what changes will occur, when they will occur, and the ability to cancel updates."
            />
            <div className="grid gap-y-8">
                {subscription.schedule.tier && (
                    <ScheduledUpdate
                        scheduleDate={subscription.schedule.tierScheduleDate as Date}
                        onCancel={(): void => cancelSchedule('tier')}
                        fromText={subscription.tier.toLowerCase()}
                        toText={subscription.schedule.tier.toLowerCase()}
                        isDisabled={updateScheduleQuery.isLoading}
                        isCancelling={cancelling === 'tier'}
                        title="Tier Change"
                    />
                )}
                {subscription.schedule.duration && (
                    <ScheduledUpdate
                        scheduleDate={subscription.schedule.durationScheduleDate as Date}
                        onCancel={(): void => cancelSchedule('duration')}
                        fromText={subscription.duration.toLowerCase()}
                        toText={subscription.schedule.duration.toLowerCase()}
                        isDisabled={updateScheduleQuery.isLoading}
                        isCancelling={cancelling === 'duration'}
                        title="Billing Period Change"
                    />
                )}
                {subscription.schedule.activeEntityAllocation && (
                    <ScheduledUpdate
                        scheduleDate={subscription.schedule.activeEntityAllocationScheduleDate as Date}
                        onCancel={(): void => cancelSchedule('quantity')}
                        fromText={`${subscription.activeEntities.allocation} active entities`}
                        toText={`${subscription.schedule.activeEntityAllocation} active entities`}
                        isDisabled={updateScheduleQuery.isLoading}
                        isCancelling={cancelling === 'quantity'}
                        title="Active Entities Change"
                    />
                )}
            </div>
        </SettingsContainer>
    );
}

type ScheduledUpdateProps = {
    title: string;
    fromText: string;
    toText: string;
    scheduleDate: Date;
    isCancelling: boolean;
    isDisabled: boolean;
    onCancel: () => void;
};

function ScheduledUpdate({ title, fromText, toText, scheduleDate, isCancelling, isDisabled, onCancel }: ScheduledUpdateProps): JSX.Element {
    return (
        <section className="text-xs flex md:flex-col lg:flex-row gap-y-2 items-start justify-between">
            <div className="grid gap-y-2">
                <div className="flex gap-x-2 items-center text-gray-600 dark:text-gray-400">
                    <p className="font-medium">{title}</p>
                    <div className="flex gap-x-1">
                        <ClockIcon className="h-4 w-4 p-0 m-0" />
                        <label>{formatDate(scheduleDate)}</label>
                    </div>
                </div>
                <div className="flex gap-x-2 items-center text-gray-800 dark:text-gray-200 capitalize">
                    <label>{fromText}</label>
                    <ArrowRightIcon className="h-4 w-4" />
                    <label>{toText}</label>
                </div>
            </div>
            <ButtonV2
                type="danger"
                size="xs"
                label={isCancelling ? 'Cancelling...' : 'Cancel Update'}
                onClick={onCancel}
                isLoading={isCancelling}
                isDisabled={isDisabled}
            />
        </section>
    );
}

export default ScheduledUpdates;
